import { SvcPrinterOption } from '../../../../general/svc-printer/svc-printer-classes';

export interface ActionPlanPrintSections {
  form?: HTMLElement;
  checklist?: HTMLElement;
  approval?: HTMLElement;
  history?: HTMLElement;
  feed?: HTMLElement;
}

const wrap = (el?: HTMLElement): HTMLElement[] => (el ? [el] : []);

/**
 * Builds a text-only checklist element for printing: one line per item showing
 * just the task text, in the same order shown on screen. The add/drag/checkbox/
 * trash controls are dropped. Reads the live input value (edit mode) or the
 * read-only span text so the printed content matches what is on screen,
 * independent of the clone losing reactive-form input values.
 */
export function buildChecklistPrintElement(liveHost?: HTMLElement): HTMLElement | undefined {
  if (!liveHost) return undefined;

  const container = document.createElement('div');
  container.className = 'action-plan-checklist-print';

  liveHost.querySelectorAll('.action-plan-checklist-item').forEach((row) => {
    const input = row.querySelector('input');
    const readonlyText = row.querySelector('.action-plan-checklist-text');
    const value = input instanceof HTMLInputElement ? input.value : (readonlyText?.textContent ?? '');

    const line = document.createElement('div');
    line.className = 'action-plan-checklist-print-item text-base text-default';
    line.textContent = value.trim() || '-';
    container.appendChild(line);
  });

  return container;
}

/**
 * Builds a compact clone of a tab section for printing. Tabs reserve fixed
 * min-heights (and scroll areas) so they fill the modal body — e.g. the approval
 * tab's `min-h-[207px]` and the history timeline's `min-height: 140px`. In print
 * those render as empty gaps between sections, so collapse every reserved
 * min/max-height (and the bottom padding on the reserved containers) so each
 * section takes only the height its content needs and the next one starts right
 * after it.
 */
export function compactSectionForPrint(liveHost?: HTMLElement): HTMLElement | undefined {
  if (!liveHost) return undefined;

  const clone = liveHost.cloneNode(true) as HTMLElement;

  const elements = [clone, ...Array.from(clone.querySelectorAll<HTMLElement>('*'))];
  elements.forEach((element) => {
    element.style.minHeight = '0';
    element.style.maxHeight = 'none';
  });

  // Space-reserving containers (approval `min-h-[207px]`, history `.svc-history-tab`
  // with min-height/height:100% + padding) must also drop their fixed height and
  // bottom padding so the section ends right after its content — not on leaf
  // elements like avatars/icons, which rely on fixed sizes.
  clone.querySelectorAll<HTMLElement>('[class*="min-h-"], .svc-history-tab').forEach((element) => {
    element.style.height = 'auto';
    element.style.paddingBottom = '0';
  });

  return clone;
}

/**
 * Builds the print sections in tab order (Action Plan, Checklist, Approval,
 * History, Feed); only "Plano de ação" is pre-selected.
 *
 * Sections are NOT flagged as separatedPage: per PO request they flow one below
 * the other on the same page (breaking to a new page only when it fills up) to
 * save paper, instead of forcing one page per section.
 */
export function buildActionPlanPrintOptions(
  translate: (key: string) => string,
  sections: ActionPlanPrintSections,
): SvcPrinterOption[] {
  return [
    { name: translate('Plano de ação'), elements: wrap(sections.form), selected: true },
    { name: translate('Checklist'), elements: wrap(sections.checklist), selected: false },
    { name: translate('Aprovação'), elements: wrap(sections.approval), selected: false },
    { name: translate('Histórico'), elements: wrap(sections.history), selected: false },
    { name: translate('Feed'), elements: wrap(sections.feed), selected: false },
  ];
}
